import { SavedLockedInfo as NotificationsUpselling } from '@/components/TemplateEditor/components/SavedLockedInfo';
import { BillingPaths, pathsParams } from '@/services/dictionaries/Paths';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { LockIcon } from '@/services/icons/LockedIcon';
import {
    getCheckoutCreatorStore,
    TemplateFormPayloadUpdate,
    TemplateFormsCheckInvalid,
} from '@/store/checkoutCreator/actions';
import { FormSettingsEnum, ProductCode } from '@dev/ee-api/ee-api-models';
import { BasicInput, SimpleToggle, Typography } from '@dev/ee-components';
import { debounce } from '@dev/ee-tools';
import { includes, remove } from 'lodash-es';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFormMultiSettingsNotificationsError } from '../../hooks/useFormMultiSettingsNotificationsError';
import { EmailsInputInfo, NotificationsWrapper, ToggleWrapper } from './styles';
import { FormMultiSettingsNotificationsProps } from './types';

export const FormMultiSettingsNotifications = ({
    blockClientId,
    selectedBlockClientIdRef,
}: FormMultiSettingsNotificationsProps): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { template } = useSelector(getCheckoutCreatorStore);
    const { isPilotPlan } = useCurrentPlanStatus();
    const clientId = blockClientId ?? selectedBlockClientIdRef.current;
    const { isEmailInputError, resetErrors, emailsInputErrorText } = useFormMultiSettingsNotificationsError(clientId);

    const templateForm = template.forms?.find((templateForm) => templateForm.blockClientId === clientId);
    const formSettings = templateForm?.formPayload?.FormSettings ?? [];
    const [notify, setNotify] = useState(includes(formSettings, FormSettingsEnum.NotifyEmails));
    const emailNotificationStoredValue = templateForm?.formPayload?.EmailsToNotifyOnAction ?? '';
    const [inputValue, setInputValue] = useState(emailNotificationStoredValue);

    useEffect(() => {
        if (!isEmailInputError) return;
        resetErrors();
        dispatch(TemplateFormsCheckInvalid());
    }, [emailNotificationStoredValue]);

    const onNotifyChange = (e) => {
        const notifyEmails = e.target.checked;
        setNotify(notifyEmails);

        const isNotifyEmailsInSettings = includes(formSettings, FormSettingsEnum.NotifyEmails);
        const isFormSettingsUpToDate = notifyEmails === isNotifyEmailsInSettings;

        if (isFormSettingsUpToDate) {
            return;
        }

        const newFormSettings = notifyEmails
            ? [...formSettings, FormSettingsEnum.NotifyEmails]
            : remove([...formSettings], (setting) => setting !== FormSettingsEnum.NotifyEmails);

        dispatch(
            TemplateFormPayloadUpdate(clientId, {
                FormSettings: newFormSettings,
                EmailsToNotifyOnAction: '',
            })
        );

        if (isEmailInputError) {
            resetErrors();
            dispatch(TemplateFormsCheckInvalid());
        }
    };

    const clickUpgradeBtn = (): void => {
        window.location.href = `${location.origin}${BillingPaths.BillingPlanList.replace(
            pathsParams.productCode,
            ProductCode.Marketing.toLowerCase()
        )}`;
    };

    const onEmailsInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const value = e?.target?.value;
        setInputValue(value);
        debounce(() => dispatch(TemplateFormPayloadUpdate(clientId, { EmailsToNotifyOnAction: value })));
    };

    return isPilotPlan ? (
        <NotificationsUpselling
            isSidebar={false}
            isFullWidth
            title={{ text: t('emailnotifications'), icon: <LockIcon width={15} height={15} /> }}
            body={{ subtitle: t('emailnotifications_subtitle'), text: t('emailnotifications_desc') }}
            button={{
                startIcon: <LockIcon width={20} height={20} />,
                text: t('upgradetounlock'),
                onClick: clickUpgradeBtn,
            }}
        />
    ) : (
        <NotificationsWrapper>
            <ToggleWrapper>
                <SimpleToggle
                    id="forms-notifications-toggle"
                    value="formsNotifications"
                    label={t('lp_creator_notifications_togglelabel')}
                    checked={notify}
                    onChange={onNotifyChange}
                />
            </ToggleWrapper>
            {notify && (
                <>
                    <EmailsInputInfo>
                        <Typography variant="body1">{t('lp_creator_notifications_emailsinputinfo')}</Typography>
                    </EmailsInputInfo>
                    <BasicInput
                        id="checkout-creator-form-notifications-email_addreses"
                        label={t('emailaddreses')}
                        placeholder={t('lp_creator_notifications_emailsinputplaceholder')}
                        type="input"
                        value={inputValue}
                        fullWidth
                        onChange={onEmailsInputChange}
                        autoComplete="off"
                        error={isEmailInputError}
                        errorMessage={emailsInputErrorText}
                        inputProps={{
                            maxLength: 1274,
                        }}
                    />
                </>
            )}
        </NotificationsWrapper>
    );
};
